import { navigate } from 'gatsby';
import { DateTime } from 'luxon';
import { map } from 'ramda';
import React, { FC } from 'react';

import Button, { IButtonProps } from '@ui/atoms/Button';
import FlexColumn from '@ui/atoms/FlexColumn';
import NavigationSection from '@ui/atoms/NavigationSection';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import UpcomingAppointmentsCard from '@ui/organisms/UpcomingAppointmentsCard';

import fullWidth from '@ui/styled/FullWidth';

import { getServiceDisplayName } from '@helpers/Appointments';

import { getAppointmentCta } from '@utils/appointment';
import { isBrowser } from '@utils/isBrowser';

import { IPageProps } from '@folx-types/flow-steps';
import { IUpcomingAppointment } from '@folx-types/scheduledAppointments';

const FullWidthButton = fullWidth<IButtonProps>(Button);
const FullWidthColumn = fullWidth(FlexColumn);

export interface IBillingCancelMembershipPageContent {
	thirdParagraph: string;
	ctaViewAppointments: string;
}

interface IBillingCancelMembershipProps extends IPageProps {
	pageContent: IBillingCancelMembershipPageContent;
	upcomingAppointments?: IUpcomingAppointment[];
}

const BillingCancelMembership: FC<IBillingCancelMembershipProps> = ({
	pageContent,
	upcomingAppointments,
}) => {
	const { thirdParagraph, ctaViewAppointments } = pageContent;

	const UpcomingAppointments = () =>
		map((upcomingAppointment: IUpcomingAppointment) => {
			const convertedDate = DateTime.fromISO(upcomingAppointment.scheduledDt);
			const localizedTime = convertedDate?.toFormat('h:mm a ZZZZ');
			const appointmentLength = upcomingAppointment?.duration ?? 0;
			const staffName = upcomingAppointment?.staffMember ?? 'Scheduled Clinician';
			const service = getServiceDisplayName(upcomingAppointment?.serviceType ?? '');
			const isWithinAppointmentWindow = Math.abs(convertedDate.diffNow('hours').hours) <= 1;

			return (
				<UpcomingAppointmentsCard
					key={upcomingAppointment.cogsworthId}
					name={staffName}
					serviceName={service}
					appointmentCta={getAppointmentCta(
						upcomingAppointment.scheduledDt,
						upcomingAppointment.visitType
					)}
					appointmentDate={`${convertedDate.toLocaleString(DateTime.DATE_MED)} at ${localizedTime}`}
					appointmentDuration={`${appointmentLength} min ${
						upcomingAppointment.visitType === 'irl' ? 'in-person' : 'virtual'
					} visit`}
					handleClick={() => {
						if (!isBrowser) {
							return;
						}

						if (isWithinAppointmentWindow) {
							window.open(upcomingAppointment.visitUrl, '__blank');
							return;
						}

						const url = `/app/member/appointments/reschedule/${upcomingAppointment.cogsworthId}`;
						navigate(url);
					}}
					visitType={upcomingAppointment.visitType}
				/>
			);
		})(upcomingAppointments ?? []);

	return (
		<FlexColumn gap="md">
			<WYSIWYGContent dangerouslySetInnerHTML={{ __html: thirdParagraph }} />
			<FullWidthColumn gap="md">
				<UpcomingAppointments />
			</FullWidthColumn>
			<NavigationSection>
				<FullWidthButton
					marginTop="lgxl"
					label={ctaViewAppointments}
					labelSize="sm"
					href="../../appointments"
				/>
			</NavigationSection>
		</FlexColumn>
	);
};

export default BillingCancelMembership;
